import { useState, useRef, useEffect } from 'react';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import moment from 'moment';
import validator from 'validator';
import {
  Typography,
  Button,
  Input,
  InputContainer,
  Select,
  DatePicker,
  BelongsToSelects,
  enqueueSnackbar,
} from '../../design-system';
import { DashboardHeader, DashboardForm, DashboardFormCustomColumn } from '../dashboard';
import { useAddContainerMutation } from '../../hooks';
import { containerValidator, getNormalFormProps } from '../../utils';
import {
  containerTypes,
  containerContents,
  MESSAGES,
  INPUT_ERRORS,
  REGEX,
  containerConditionOptions,
} from '../../const';
import { i18n } from '../../i18n';
import { useStore } from '../../context';
import { RefreshPageActions } from '../../store/actions/refresh-page';
import { handleImportExcelClick } from './handle-import-excel-click';

let formProps: any = {};

const getSubmitProps = () => {
  const props: any = {
    clientId: formProps.clientId,
    yardId: formProps.yardId,
  };

  const errors: any = {
    completedAt: formProps.completedAt.every((date: any) =>
      moment(date).set('hour', 0).isBefore(moment().set('hour', 1)),
    ),
    hasCompletedAt: formProps.completedAt.every((date: any) => !!date),
  };

  const validators: any = {
    containerNumber: formProps.containerNumber.every((container: any) =>
      containerValidator(container),
    ),
  };

  if (
    [
      ...Object.values(props),
      ...Object.values(errors),
      ...Object.values(validators),
      ...Object.values(formProps.validators).map((val) => !val),
    ].some((val) => !val)
  ) {
    return null;
  }

  delete formProps.isSubmitClicked;

  formProps.completedAt = formProps.completedAt.map((date: any) =>
    moment(date).set({
      hour: 12,
      minute: 0,
      second: 0,
      millisecond: 0,
    }),
  );

  return getNormalFormProps(formProps);
};

export const ContainersAddHeader = ({ count, showOnlySubmit }: any) => {
  const { dispatch } = useStore();
  const { mutate } = useAddContainerMutation();

  const handleSubmitClick = () => {
    formProps.onSubmitClick();
    const props = getSubmitProps();
    if (!props) return;

    mutate(props, {
      onSuccess: (res: any) => {
        if (res.some((data: any) => data.data.statusCode === 409)) {
          enqueueSnackbar(
            MESSAGES.CONTAINERS.WARNING.ALREADY_EXISTS(
              res.map((data: any) => data.data.containers.join(', ')).join(', '),
            ),
            'error',
          );
          return;
        }

        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.GATED_IN, 'success');
        dispatch({ type: RefreshPageActions.SET_REFRESH_PAGE });
      },
    });
  };

  return (
    <DashboardHeader>
      {!showOnlySubmit && (
        <>
          <Typography variant={'textXL'} weight={'bold'}>
            {i18n.t('pages.containersAdd.title')}
            {count && count > 1 ? `: ${count}` : ''}
          </Typography>
          <Button size={'medium'} data-color={'white'} to={-1}>
            {i18n.t('button.goBack')}
          </Button>
        </>
      )}
      {showOnlySubmit && <span />}
      <Button size={'medium'} onClick={handleSubmitClick}>
        {i18n.t('button.submit')}
      </Button>
    </DashboardHeader>
  );
};

export const ContainersAddForm = ({ setCount }: any) => {
  const [yardCompanyName, setYardCompanyName] = useState();
  const yardCompanyId = useRef();
  const [yardName, setYardName] = useState();
  const yardId = useRef();
  const [clientName, setClientName] = useState();
  const clientId = useRef();
  const [containerNumber, setContainerNumber] = useState(['']);
  const [driverFirstName, setDriverFirstName] = useState('');
  const [driverLastName, setDriverLastName] = useState('');
  const [licensePlateNumber, setLicensePlateNumber] = useState('');

  const [containerType, setContainerType] = useState([containerTypes[0].value]);
  const [condition, setCondition] = useState(['']);
  const [containerContent, setContainerContent] = useState([containerContents[0].value]);
  const [completedAt, setCompletedAt] = useState([null]);
  const [, setUpdate] = useState<any>(Math.random());

  const validators = {
    driverFirstName:
      !!driverFirstName &&
      (!validator.isLength(driverFirstName, { max: 30 }) || !REGEX.USER_NAME.test(driverFirstName)),
    driverLastName:
      !!driverLastName &&
      (!validator.isLength(driverLastName, { max: 30 }) || !REGEX.USER_NAME.test(driverLastName)),
    licensePlateNumber: licensePlateNumber
      ? !validator.isLength(licensePlateNumber, { min: 4, max: 10 })
        ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER_LENGTH
        : !REGEX.LICENSE_PLATE_NUMBER.test(licensePlateNumber)
        ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER
        : ''
      : '',
  };

  useEffect(() => {
    if (!formProps) return;

    formProps.isSubmitClicked = false;
    setUpdate(Math.random());
  }, []);

  useEffect(() => {
    formProps = {
      ...formProps,
      containerNumber: containerNumber,
      yardId: yardId.current,
      clientId: clientId.current,
      containerType: containerType,
      condition: condition || undefined,
      isLoaded: containerContent,
      completedAt: completedAt,
      driverFirstName,
      driverLastName,
      licensePlateNumber,
      validators,
      onSubmitClick: () => {
        formProps.isSubmitClicked = true;
        setUpdate(Math.random());
      },
    };
  }, [
    containerNumber,
    yardCompanyName,
    yardCompanyId.current,
    yardName,
    yardId.current,
    clientName,
    clientId.current,
    containerType,
    condition,
    containerContent,
    completedAt,
    driverFirstName,
    driverLastName,
    licensePlateNumber,
  ]);

  const handlePlusClick = () => {
    setCount((prev: number) => prev + 1);

    setContainerNumber([...containerNumber, '']);
    setContainerType([...containerType, containerTypes[0].value]);
    setCondition([...condition, '']);
    setContainerContent([...containerContent, containerContents[0].value]);
    setCompletedAt([...completedAt, null]);
    setUpdate(Math.random());
  };

  const handleDeleteClick = (i: number) => {
    setCount((prev: number) => prev - 1);

    const filteredContainerNumber = containerNumber.filter((_: any, index: any) => i !== index);
    const filteredContainerType = containerType.filter((_: any, index: any) => i !== index);
    const filteredCondition = condition.filter((_: any, index: any) => i !== index);
    const filteredContainerContent = containerContent.filter((_: any, index: any) => i !== index);
    const filteredCompletedAt = completedAt.filter((_: any, index: any) => i !== index);

    setContainerNumber(filteredContainerNumber);
    setContainerType(filteredContainerType);
    setCondition(filteredCondition);
    setContainerContent(filteredContainerContent);
    setCompletedAt(filteredCompletedAt);
    setUpdate(Math.random());
  };

  return (
    <DashboardForm paperSize={'full-size'}>
      <DashboardFormCustomColumn
        id={'import-excel-row'}
        columns={'max-content 1fr'}
        align={'center'}
      >
        <Button
          size={'medium'}
          onClick={() => {
            handleImportExcelClick({
              setCount,
              setContainerNumber,
              setContainerType,
              setCondition,
              setContainerContent,
              setCompletedAt,
            });
            setUpdate(Math.random());
          }}
        >
          {i18n.t('button.importExcel')}
        </Button>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('label.columns')}: |{i18n.t('label.containerNumber')}|
          {i18n.t('label.containerType')}|{i18n.t('label.gatedInDate')}|
        </Typography>
      </DashboardFormCustomColumn>
      <BelongsToSelects
        handleYardCompanyChange={(obj) => {
          yardCompanyId.current = obj.id;
          setYardCompanyName(obj.label);
        }}
        handleYardChange={(obj) => {
          yardId.current = obj.id;
          setYardName(obj.label);
        }}
        handleClientChange={(obj) => {
          clientId.current = obj.id;
          setClientName(obj.label);
        }}
        isSubmitClicked={formProps.isSubmitClicked}
      />
      {containerNumber.map((_, i) => (
        <DashboardFormCustomColumn
          key={i}
          columns={
            'calc(100%/5 - 2.8rem*(4/5)) calc(100%/5 - 2.8rem*(4/5))  calc(100%/5 - 2.8rem*(4/5)) calc(100%/5 - 2.8rem*(4/5)) 1fr max-content'
          }
          align={'start'}
        >
          <InputContainer
            label={i18n.t('label.containerNumber') + '*'}
            data-size={'medium'}
            value={containerNumber[i]}
            onChange={(e) => {
              const newContainerNumber = containerNumber;
              newContainerNumber[i] = e.target.value;
              setContainerNumber([...newContainerNumber]);
            }}
            error={
              !!containerNumber[i] && !containerValidator(containerNumber[i])
                ? true
                : formProps.isSubmitClicked && !containerNumber[i]
            }
            helperText={
              !!containerNumber[i] && !containerValidator(containerNumber[i])
                ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER
                : formProps.isSubmitClicked && !containerNumber[i]
                ? INPUT_ERRORS.REQUIRED.CONTAINER_NUMBER
                : ''
            }
          />
          <Select
            label={i18n.t('label.containerType') + '*'}
            options={containerTypes}
            value={containerType[i]}
            onSelect={(value: any) => {
              const newContainerType = containerType;
              newContainerType[i] = value;
              setContainerType([...newContainerType]);
            }}
          />
          <Select
            label={i18n.t('label.condition')}
            options={containerConditionOptions}
            value={condition[i]}
            onSelect={(value: any) => {
              const newCondition = condition;
              newCondition[i] = value;
              setCondition([...newCondition]);
            }}
            withClearButton
          />
          <Select
            label={i18n.t('label.containerContent')}
            options={containerContents}
            value={containerContent[i]}
            onSelect={(value: any) => {
              const newContainerContent = containerContent;
              newContainerContent[i] = value;
              setContainerContent([...newContainerContent]);
            }}
          />
          <DatePicker
            label={i18n.t('label.gatedInDate') + '*'}
            data-size={'medium'}
            defaultValue={completedAt[i]}
            onChange={(value: any) => {
              const newCompletedAt = completedAt;
              newCompletedAt[i] = value;
              setCompletedAt([...newCompletedAt]);
            }}
            maxDate={moment()}
            error={formProps.isSubmitClicked && !completedAt[i]}
            helperText={formProps.isSubmitClicked && !completedAt[i] && INPUT_ERRORS.REQUIRED.DATE}
          />
          {i !== containerNumber.length - 1 && (
            <Button
              className={'row-button-square'}
              size={'medium'}
              data-color={'destructiveSecondary'}
              isSquare={true}
              onClick={() => handleDeleteClick(i)}
            >
              <DeleteIcon />
            </Button>
          )}
          {i === containerNumber.length - 1 && (
            <Button
              className={'row-button-square'}
              size={'medium'}
              data-color={'secondary'}
              isSquare={true}
              onClick={handlePlusClick}
            >
              <AddIcon />
            </Button>
          )}
        </DashboardFormCustomColumn>
      ))}
      <DashboardFormCustomColumn columns={'1fr 1fr 1fr'}>
        <Input
          label={i18n.t('label.licensePlateNumber')}
          data-size={'medium'}
          defaultValue={licensePlateNumber}
          onChange={(e) => setLicensePlateNumber(e.target.value)}
          error={!!validators.licensePlateNumber}
          helperText={validators.licensePlateNumber}
        />
        <Input
          label={i18n.t('label.driverFirstName')}
          data-size={'medium'}
          defaultValue={driverFirstName}
          onChange={(e) => setDriverFirstName(e.target.value)}
          error={validators.driverFirstName}
          helperText={validators.driverFirstName ? INPUT_ERRORS.NOT_VALID.DRIVER_NAME : ''}
        />
        <Input
          label={i18n.t('label.driverLastName')}
          data-size={'medium'}
          defaultValue={driverLastName}
          onChange={(e) => setDriverLastName(e.target.value)}
          error={validators.driverLastName}
          helperText={validators.driverLastName ? INPUT_ERRORS.NOT_VALID.DRIVER_NAME : ''}
        />
      </DashboardFormCustomColumn>
    </DashboardForm>
  );
};
